import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DriveTimeTrafficType, MapUnits, RoutingMode } from '@sas-dvr/ltjs-commons';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { Divider } from '@sas/nova-commons/components/Divider';
import { Icon } from '@sas/nova-commons/components/Icon';
import { AddTab } from '@sas/react-svg';
import { Input, useInputValidationAdapter, } from '@sas/nova-commons/components/Input';
import { Label } from '@sas/nova-commons/components/Label';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { NumericStepper } from '@sas/nova-commons/components/NumericStepper';
import { getUpdatedOpenSectionsOnToggle, WindowShade, } from '@sas/nova-commons/components/WindowShade';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { Add, Delete, GeoUIAreaAddress, GeoUIAreaPinLocation, LinkBroken, Overflow, Refresh, UserSession, } from '@sas/react-svg';
import cx from 'classnames';
import { Fragment, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../../../locale/I18nContext';
import { isValidLocation, ObjectSearchState, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { useLatestRef, useOnUnmount } from '../../../hooks';
import { useMinPanelHeight } from '../../hooks/useMinPanelHeight';
import { PanelHeader } from '../components/PanelHeader';
import { PanelSearchField } from '../components/PanelSearchField';
import { TravelTypeField } from '../components/TravelTypeField';
import { Demographics } from './Demographics';
import { Toolbar } from '@sas/nova-commons/components/Toolbar';
import { DemographicsResults } from './DemographicsResults';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { FeatureColorPicker } from '../components/FeatureColorPicker';
import './GeographicAreaPanel.css';
import cn from './GeographicAreaPanel.css.json';
const useGeographicAreaPanelId = createUseHtmlId('GeographicAreaPanel');
export const GeographicAreaPanelBase = (props) => {
    var _a, _b, _c, _d, _e, _f;
    // Hooks
    // =============================================================================
    const localize = useI18n();
    const dt = useDesignTokens();
    // Local states
    // ===============================================================================
    const [distanceValuesInError, setDistanceValuesInError] = useState([]);
    const maxBufferDistance = props.area.maxBufferDistance || 99;
    const allowMapRendering = isValidLocation(props.area.latLon) &&
        !props.area.isRendered &&
        distanceValuesInError.length === 0 &&
        ((_a = props.area.amount) === null || _a === void 0 ? void 0 : _a.every((a) => a <= maxBufferDistance));
    const [demographicsDialogState, setDemographicsDialogState] = useState({
        featureIndex: props.featureIndex,
        resultsDialogIsOpen: false,
        selectorDialogIsOpen: false,
    });
    const [demographicsOpenSections, setDemographicsOpenSections] = useState([0]);
    const headingId = useGeographicAreaPanelId();
    const validateLocation = useRef(true);
    const inputRef = useRef(null);
    const isTimeTravel = props.area.mode === RoutingMode.DRIVING_TIME ||
        props.area.mode === RoutingMode.TRUCKING_TIME ||
        props.area.mode === RoutingMode.WALKING_TIME;
    const headerRef = useRef(null);
    const footerRef = useRef(null);
    useMinPanelHeight(headerRef, footerRef, (minPanelHeight) => {
        props.actions.update(props.id, { minPanelHeight });
    });
    /* basic validator for name input */
    const nameInputValidator = useInputValidationAdapter({
        isRequired: true,
        minLength: 1,
        initialValue: props.area.name,
    });
    {
        const refHandleChange = useLatestRef(nameInputValidator.handleChange);
        useEffect(() => {
            refHandleChange.current({ value: props.area.name });
        }, [refHandleChange, props.area.name]);
    }
    useOnUnmount(() => {
        // Disable pin mode on unmount
        props.actions.update(props.id, {
            pinModeEnabled: false,
            locationCandidate: undefined,
            searchHints: [],
        });
    });
    const distanceSteppers = () => {
        const stepperRows = [];
        const distances = (props.area.amount || [5]).filter((d, i) => i === 0 || d <= maxBufferDistance);
        const onDistanceChange = (index, distance) => {
            const inErrorIndex = distanceValuesInError.indexOf(index);
            if (distance === null || distance === undefined) {
                // invalid or no number entered - mark as error
                if (inErrorIndex === -1) {
                    setDistanceValuesInError((distanceValuesInError) => [...distanceValuesInError, index]);
                }
                return;
            }
            else if (inErrorIndex !== -1) {
                // distance value now valid - remove the error flag
                const valuesInError = [...distanceValuesInError];
                valuesInError.splice(inErrorIndex, 1);
                setDistanceValuesInError(valuesInError);
            }
            if (distance === -1) {
                // delete
                distances.splice(index, 1);
            }
            else {
                // update
                distances[index] = Math.min(distance, maxBufferDistance);
            }
            props.actions.notifyUpdateArea(props.id, {
                index: props.featureIndex,
                update: {
                    amount: distances,
                },
            });
        };
        distances.forEach((distance, index) => {
            const minimum = index > 0 ? distances[index - 1] + 0.001 : 0.001; // minimum is the previous distance value
            const maximum = Math.min(index + 1 !== distances.length ? distances[index + 1] - 0.001 : maxBufferDistance, // maximum is the following distance value
            maxBufferDistance);
            const canAddMore = index + 1 === distances.length && index < 2 && distance < maximum; // we do not support more than three
            const canDelete = index > 0;
            stepperRows.push(_jsxs(Flow, Object.assign({ spacing: 'xs', orientation: 'horizontal', className: cn.formField }, { children: [_jsx(DistanceStepper, { minimum: minimum, maximum: maximum, distance: distance, onCommit: (value) => onDistanceChange(index, value) }), canDelete && (_jsx(Button, { icon: { icon: Delete }, variant: ButtonVariant.Icon, onPress: () => onDistanceChange(index, -1), tooltip: localize({ key: 'GeographicAreaPanel.distance.delete.txt' }) })), canAddMore && (_jsx(Button, { icon: { icon: Add }, variant: ButtonVariant.Icon, onPress: () => onDistanceChange(index + 1, distance + 5), tooltip: localize({
                            key: isTimeTravel
                                ? 'GeographicAreaPanel.time.add.txt'
                                : 'GeographicAreaPanel.distance.add.txt',
                        }) }))] }), `${index}`));
        });
        return stepperRows;
    };
    useEffect(() => {
        var _a;
        let linkedItem = undefined;
        if (props.linkingEnabled) {
            linkedItem = (_a = props.features) === null || _a === void 0 ? void 0 : _a.find((feature, index) => {
                return feature.type === 'pin' && index === props.area.linkedPinIndex;
            });
        }
        if (linkedItem && linkedItem.location !== props.area.location) {
            props.actions.notifyUpdatePin(props.id, {
                index: props.featureIndex,
                update: {
                    latLon: (linkedItem === null || linkedItem === void 0 ? void 0 : linkedItem.latLon) || props.area.latLon,
                    location: (linkedItem === null || linkedItem === void 0 ? void 0 : linkedItem.location) || props.area.location,
                },
            });
        }
    }, [
        props.area.linkedPinIndex,
        props.features,
        props.area.location,
        props.linkingEnabled,
        props.actions,
        props.area.latLon,
        props.featureIndex,
        props.id,
    ]);
    const tokens = renderInlineCssVars({ 'spacing-m': dt.tokens['space-base-m'], bg: dt.reportTokens.backgrounds.panes.bg }, 'geoArea');
    const menuSections = useMemo(() => {
        let sections = [
            {
                items: [
                    {
                        text: localize({ key: 'GeographicAreaPanel.menu.clear.txt' }),
                    },
                ],
            },
        ];
        if (props.linkingEnabled) {
            sections = sections.concat([
                {
                    items: [
                        {
                            text: localize({ key: 'GeographicAreaPanel.menu.link.txt' }),
                            checked: props.autoLinkToPins,
                        },
                        {
                            text: localize({ key: 'GeographicAreaPanel.menu.unlink.txt' }),
                            disabled: !props.area.linkedPinIndex,
                        },
                    ],
                },
            ]);
        }
        // add distance unit selections
        sections = sections.concat([
            {
                items: [
                    {
                        text: localize({ key: 'TravelTypeField.distanceTypes.distance.unit.txt' }),
                        subSections: [
                            {
                                items: [
                                    {
                                        text: localize({ key: 'GeographicAreaPanel.distanceUnits.miles.txt' }),
                                        checked: props.area.unit === MapUnits.MILE,
                                    },
                                    {
                                        text: localize({ key: 'GeographicAreaPanel.distanceUnits.kilometers.txt' }),
                                        checked: props.area.unit === MapUnits.KILOMETER,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        text: localize({ key: 'GeographicAreaPanel.menu.delete.txt' }),
                    },
                ],
            },
        ]);
        return sections;
    }, [
        props.linkingEnabled,
        props.autoLinkToPins,
        props.area.linkedPinIndex,
        props.area.unit,
        localize,
    ]);
    // clear geographic area if search term has been cleared
    useEffect(() => {
        var _a;
        if (((_a = props.area.location) === null || _a === void 0 ? void 0 : _a.length) === 0 && props.area.isRendered) {
            props.actions.notifyClearArea(props.id, { index: props.featureIndex });
        }
    }, [props.area.location, props.actions, props.area.isRendered, props.id, props.featureIndex]);
    return (_jsxs("section", Object.assign({ className: cn.formContainer, style: tokens, tabIndex: -1, "aria-labelledby": headingId }, { children: [_jsx(PanelHeader, { id: props.id, headingId: headingId, ref: headerRef, type: 'area', text: localize({ key: 'GeographicAreaPanel.title.txt' }), onMenuItemSelected: (location) => {
                    const section = location[0];
                    if (section.length === 2) {
                        switch (section[0]) {
                            case 0: // clear geographic area
                                props.actions.notifyClearArea(props.id, { index: props.featureIndex });
                                break;
                            case 1:
                                switch (section[1]) {
                                    case 0:
                                        if (props.linkingEnabled) {
                                            // toggle auto-link global setting
                                            props.actions.toggleAutoLinkToPins(props.id);
                                        }
                                        else {
                                            // toggle distance unit
                                            props.actions.notifyUpdateArea(props.id, {
                                                index: props.featureIndex,
                                                update: {
                                                    unit: location[1][1] === 0 ? MapUnits.MILE : MapUnits.KILOMETER,
                                                },
                                            });
                                        }
                                        break;
                                    case 1:
                                        if (props.linkingEnabled) {
                                            // unlink this feature from the pin
                                            props.actions.notifyUpdateArea(props.id, {
                                                index: props.featureIndex,
                                                update: {
                                                    linkedPinIndex: undefined,
                                                },
                                            });
                                        }
                                        else {
                                            // Delete geographic area
                                            validateLocation.current = false;
                                            props.actions.notifyDeletePins(props.id, [props.featureIndex]);
                                        }
                                        break;
                                }
                                break;
                            case 2:
                                // toggle distance unit (options moves to 3rd menu position if linking is enabled)
                                props.actions.notifyUpdateArea(props.id, {
                                    index: props.featureIndex,
                                    update: {
                                        unit: section[1] === 0 ? MapUnits.MILE : MapUnits.KILOMETER,
                                    },
                                });
                                break;
                            case 3:
                                // Delete geographic area
                                validateLocation.current = false;
                                props.actions.notifyDeletePins(props.id, [props.featureIndex]);
                                break;
                        }
                    }
                }, menuSections: menuSections }), _jsx("div", Object.assign({ className: cn.formScrollContainer }, { children: _jsxs(Flow, Object.assign({ className: cn.formContent, orientation: "vertical", spacing: "s" }, { children: [_jsx(Label, { children: localize({ key: 'GeographicAreaPanel.label.txt' }) }), _jsxs(Flow, Object.assign({ className: cx(cn.formField, cn.formFieldCentered), orientation: "horizontal", spacing: "s" }, { children: [_jsx(Input, { value: nameInputValidator.value, onBlur: nameInputValidator.handleBlur, onCommit: nameInputValidator.handleCommit, message: (_b = nameInputValidator.errors) === null || _b === void 0 ? void 0 : _b.message, status: (_c = nameInputValidator.errors) === null || _c === void 0 ? void 0 : _c.status, onCancel: nameInputValidator.handleCancel, onChange: (event) => {
                                        nameInputValidator.handleChange(event);
                                        props.actions.notifyUpdateArea(props.id, {
                                            index: props.featureIndex,
                                            update: {
                                                name: event.value,
                                            },
                                        });
                                    }, className: cn.formField }), _jsx(FeatureColorPicker, { id: props.id, feature: props.area, featureIndex: props.featureIndex, onColorChange: (color) => {
                                        props.actions.notifyUpdateArea(props.id, {
                                            index: props.featureIndex,
                                            update: { color },
                                        });
                                    } })] })), _jsx(TravelTypeField, { canDoPremiumServices: props.canDoPremiumServices, feature: props.area, onTravelTypeChange: (mode) => {
                                props.actions.notifyUpdateArea(props.id, {
                                    index: props.featureIndex,
                                    update: {
                                        mode,
                                    },
                                });
                            }, onTrafficTypeChange: (type) => {
                                var _a, _b;
                                const now = new Date();
                                props.actions.notifyUpdateArea(props.id, {
                                    index: props.featureIndex,
                                    update: Object.assign(Object.assign({ traffic: type }, (type === DriveTimeTrafficType.HISTORICAL_AVERAGE && {
                                        trafficTime: {
                                            day: now.getDay(),
                                            hour: (_a = now.getHours()) !== null && _a !== void 0 ? _a : 0,
                                            minute: (_b = now.getMinutes()) !== null && _b !== void 0 ? _b : 0,
                                        },
                                    })), (type === DriveTimeTrafficType.LIVE && {
                                        trafficTime: {
                                            hour: 0,
                                        },
                                    })),
                                });
                            }, onTrafficTimeChange: (trafficTime) => {
                                props.actions.notifyUpdateArea(props.id, {
                                    index: props.featureIndex,
                                    update: {
                                        trafficTime,
                                    },
                                });
                            }, onTrafficRefresh: (refreshAll) => {
                                if (refreshAll) {
                                    props.actions.notifyDrawAll(props.id);
                                }
                                else {
                                    props.actions.notifyDrawArea(props.id, props.featureIndex);
                                }
                            } }), _jsx(Label, { children: localize({
                                key: isTimeTravel
                                    ? 'GeographicAreaPanel.time.minutes.txt'
                                    : props.area.unit === MapUnits.MILE
                                        ? 'GeographicAreaPanel.distance.miles.txt'
                                        : 'GeographicAreaPanel.distance.kilometer.txt',
                            }) }), distanceSteppers(), _jsx(Suspense, Object.assign({ fallback: null }, { children: _jsx(Label, Object.assign({ helpPopup: {
                                    library: 'vaeua-gui-eua',
                                    contentKey: 'react.va.geo.search.geographicAreaLocation',
                                } }, { children: localize({ key: 'GeographicAreaPanel.location.txt' }) })) })), _jsxs(Flow, Object.assign({ spacing: 's', orientation: 'horizontal' }, { children: [_jsx(Icon, { icon: props.area.linkedPinIndex !== undefined && props.linkingEnabled
                                        ? GeoUIAreaPinLocation
                                        : GeoUIAreaAddress, className: cn.formIconLarge }), _jsx("div", Object.assign({ className: cn.formField, style: { width: 'calc(100% - 2rem)' } }, { children: _jsx(PanelSearchField, { id: props.id, ref: inputRef, includePinResults: true, hintIconOverride: AddTab, feature: props.area, locationCandidate: props.locationCandidate, locationPopoverTitle: localize({ key: 'LocationPopover.area.title.txt' }), onInvalidateLocation: () => props.actions.notifyUpdateArea(props.id, {
                                            index: props.featureIndex,
                                            update: { latLon: undefined, location: undefined },
                                        }), onUpdateHints: ({ searchTerm }) => {
                                            props.actions.notifyUpdateHints(props.id, {
                                                featureIndex: props.featureIndex,
                                                searchTerm,
                                            });
                                        }, onApplyHint: ({ hint, searchTerm }) => {
                                            props.actions.notifyApplyHint(props.id, {
                                                featureIndex: props.featureIndex,
                                                hint,
                                                searchTerm,
                                            });
                                        }, onApplyPin: ({ pin }) => {
                                            props.actions.notifyUpdateArea(props.id, {
                                                index: props.featureIndex,
                                                update: {
                                                    location: pin.location,
                                                    latLon: pin.latLon,
                                                },
                                            });
                                        }, onUpdatePinMode: (enabled) => {
                                            props.actions.setPinModeEnabled(props.id, enabled);
                                        }, onConfirm: () => {
                                            var _a, _b;
                                            props.actions.update(props.id, {
                                                pinModeEnabled: false,
                                                locationCandidate: undefined,
                                            });
                                            props.actions.notifyUpdateArea(props.id, {
                                                index: props.featureIndex,
                                                update: Object.assign({ latLon: props.locationCandidate.latLon, location: (_a = props.locationCandidate.location) !== null && _a !== void 0 ? _a : undefined }, (((_b = props.locationCandidate) === null || _b === void 0 ? void 0 : _b.country) && {
                                                    country: { id3: props.locationCandidate.country },
                                                })),
                                            });
                                        }, onCancel: () => {
                                            props.actions.update(props.id, {
                                                pinModeEnabled: false,
                                                locationCandidate: undefined,
                                            });
                                        }, onOutsideClick: () => {
                                            props.actions.update(props.id, {
                                                locationCandidate: undefined,
                                            });
                                        } }) })), props.area.linkedPinIndex !== undefined && props.linkingEnabled && (_jsx(Button, { icon: { icon: LinkBroken }, variant: ButtonVariant.Icon, onPress: () => {
                                        props.actions.notifyUpdateArea(props.id, {
                                            index: props.featureIndex,
                                            update: {
                                                linkedPinIndex: undefined,
                                            },
                                        });
                                    } }))] })), props.canDoPremiumServices ? (_jsxs(Fragment, { children: [_jsx(WindowShade, { className: cn.formField, sections: [
                                        {
                                            header: localize({ key: 'GeographicAreaPanel.demographics.txt' }),
                                            renderToolbar: () => {
                                                var _a;
                                                return (_jsx(Toolbar, { items: [
                                                        {
                                                            type: 'MenuButton',
                                                            variant: ButtonVariant.Light,
                                                            icon: { icon: Overflow },
                                                            sections: [
                                                                {
                                                                    items: [
                                                                        {
                                                                            text: localize({
                                                                                key: 'GeographicAreaPanel.demographics.menu.expand.txt',
                                                                            }),
                                                                        },
                                                                        {
                                                                            text: localize({
                                                                                key: 'GeographicAreaPanel.demographics.menu.edit.txt',
                                                                            }),
                                                                        },
                                                                        {
                                                                            text: localize({
                                                                                key: 'GeographicAreaPanel.demographics.menu.delete.txt',
                                                                            }),
                                                                        },
                                                                    ],
                                                                },
                                                            ],
                                                            onItemSelected: (location) => {
                                                                const section = location[0];
                                                                if (section.length === 2) {
                                                                    if (section[0] === 0) {
                                                                        switch (section[1]) {
                                                                            case 0: // expand
                                                                                setDemographicsDialogState((prevState) => (Object.assign(Object.assign({}, prevState), { resultsDialogIsOpen: true })));
                                                                                break;
                                                                            case 1: // edit
                                                                                setDemographicsDialogState((prevState) => (Object.assign(Object.assign({}, prevState), { selectorDialogIsOpen: true })));
                                                                                break;
                                                                            case 2: // delete
                                                                                props.actions.setSelectedDemographicMeasures(props.id, {
                                                                                    index: props.featureIndex,
                                                                                    selectedMeasures: [],
                                                                                });
                                                                                break;
                                                                        }
                                                                    }
                                                                }
                                                            },
                                                            disabled: Object.keys(((_a = props.area.demographics) === null || _a === void 0 ? void 0 : _a.data) || {}).length === 0,
                                                        },
                                                    ] }));
                                            },
                                            content: ((_d = props.area.demographics) === null || _d === void 0 ? void 0 : _d.selectedMeasures) &&
                                                props.area.demographics.selectedMeasures.length > 0 &&
                                                Object.keys(((_e = props.area.demographics) === null || _e === void 0 ? void 0 : _e.data) || {}).length === 0 ? (_jsxs(Flow, Object.assign({ spacing: 's', orientation: 'vertical', className: cn.formWindowShadeSection }, { children: [_jsx(Label, Object.assign({ className: cn.formDemographicsReloadLabel }, { children: localize({ key: 'GeographicAreaPanel.demographics.expired.txt' }) })), _jsx(Button, { icon: { icon: Refresh }, variant: ButtonVariant.Light, text: localize({
                                                            key: 'GeographicAreaPanel.demographics.reload.txt',
                                                        }), onPress: () => {
                                                            var _a, _b;
                                                            props.actions.setSelectedDemographicMeasures(props.id, {
                                                                index: props.featureIndex,
                                                                selectedMeasures: ((_b = (_a = props.area) === null || _a === void 0 ? void 0 : _a.demographics) === null || _b === void 0 ? void 0 : _b.selectedMeasures) || [],
                                                            });
                                                        } })] }))) : Object.keys(((_f = props.area.demographics) === null || _f === void 0 ? void 0 : _f.data) || {}).length === 0 ? (_jsx(Button, { text: localize({ key: 'GeographicAreaPanel.demographics.set.txt' }), icon: { icon: UserSession }, variant: ButtonVariant.Light, onPress: () => setDemographicsDialogState((prevState) => (Object.assign(Object.assign({}, prevState), { selectorDialogIsOpen: true }))), disabled: !props.area.isRendered ||
                                                    !props.area.demographics ||
                                                    !props.area.country })) : (_jsx(DemographicsResults, { id: props.id, className: cn.formWindowShadeSection, featureIndex: props.featureIndex, type: 'carousel' })),
                                        },
                                    ], openSections: demographicsOpenSections, onToggleSection: (sectionIndex) => setDemographicsOpenSections((openSections) => {
                                        return getUpdatedOpenSectionsOnToggle(openSections, sectionIndex);
                                    }) }), _jsx(Demographics, { id: props.id, dialogOpenState: demographicsDialogState, setDialogOpenState: setDemographicsDialogState })] })) : null] })) })), _jsx(Divider, { variant: 'standard' }), _jsx("div", Object.assign({ ref: footerRef, className: cn.footer }, { children: _jsx(Button, { className: cn.footerButton, disabled: !allowMapRendering, text: localize({
                        key: props.area.isRendered === undefined
                            ? 'GeographicAreaPanel.draw.txt'
                            : 'GeographicAreaPanel.update.txt',
                    }), onPress: () => {
                        props.actions.notifyDrawArea(props.id, props.featureIndex);
                    } }) }))] })));
};
const DistanceStepper = (props) => {
    const [prevDistance, setPrevDistance] = useState(props.distance);
    const [value, setValue] = useState(props.distance);
    if (props.distance !== prevDistance) {
        // Distance changed, update the value
        setPrevDistance(props.distance);
        setValue(props.distance);
        return null;
    }
    return (_jsx(NumericStepper, { minimum: parseFloat(props.minimum.toFixed(3)), maximum: parseFloat(props.maximum.toFixed(3)), stepSize: 1, required: true, onChange: (event) => setValue(event.displayValue), onCommit: (event) => props.onCommit(event.value), className: cn.formField, editMode: "combo", value: value, style: { width: '10rem' }, numberFormatOptions: { maximumFractionDigits: 3 } }));
};
export const GeographicAreaPanel = connect((state, props) => {
    var _a;
    const { features, autoLinkToPins, canDoPremiumServices, demographicCountries, linkingEnabled, pinModeEnabled, locationCandidate, } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const area = features === null || features === void 0 ? void 0 : features[props.featureIndex];
    if (!area || area.type !== 'area') {
        throw new Error('Area not found');
    }
    return {
        area,
        autoLinkToPins,
        features,
        canDoPremiumServices,
        demographicCountries,
        linkingEnabled,
        pinModeEnabled,
        locationCandidate,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(GeographicAreaPanelBase);
